<template>
  <div v-if="checklist" class="p-6">
    <!-- Layout Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
      <!-- Tree View Sidebar -->
      <div class="lg:col-span-1">
        <ChecklistTreeView :checklist="checklist" />
      </div>

      <!-- Main Content -->
      <div class="lg:col-span-3">
        <div class="bg-white rounded-lg shadow-lg p-6">
          <div class="flex items-center justify-between mb-6">
            <button 
              @click="goBack" 
              class="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition duration-200 shadow-sm flex items-center gap-2"
            >
              <span class="material-icons">arrow_back</span>
              Volver al Listado
            </button>
          </div>

          <h2 class="text-3xl font-bold mb-6 text-gray-800">Editar Checklist</h2>
          
          <!-- Add number field -->
          <div class="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <!-- New Rev Field -->
            <div class="md:col-span-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Rev <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center gap-1">
                <button 
                  type="button" 
                  @click="decrementRev" 
                  class="inline-flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <span class="material-icons">remove</span>
                </button>
                <input 
                  type="number" 
                  min="1"
                  v-model.number="checklist.rev" 
                  class="w-16 p-2 text-center border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none"
                />
                <button 
                  type="button" 
                  @click="incrementRev" 
                  class="inline-flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <span class="material-icons">add</span>
                </button>
              </div>
              <p v-if="showErrors && !checklist.rev" class="mt-1 text-sm text-red-500">
                La revisión es requerida
              </p>
            </div>

            <!-- Existing Number Field -->
            <div class="md:col-span-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Número <span class="text-red-500">*</span>
              </label>
              <input 
                data-field="number"
                class="w-full p-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none"
                :class="{'border-red-500': showErrors && !checklist.number}"
                v-model="checklist.number" 
                placeholder="Ingrese número" 
                required
                ref="numberInput"
              />
              <p v-if="showErrors && !checklist.number" class="mt-1 text-sm text-red-500">
                El número es requerido
              </p>
            </div>
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Título <span class="text-red-500">*</span>
              </label>
              <input 
                data-field="title"
                class="w-full p-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none"
                :class="{'border-red-500': showErrors && !checklist.title}"
                v-model="checklist.title" 
                placeholder="Ingrese título" 
                required
                ref="titleInput"
              />
              <p v-if="showErrors && !checklist.title" class="mt-1 text-sm text-red-500">
                El título es requerido
              </p>
            </div>
          </div>

          <div class="space-y-6">
            <!-- Regular Items Section -->
            <div class="space-y-4 mb-8">
              <h3 class="text-2xl font-bold text-gray-700">Elementos</h3>
              
              <draggable 
                v-if="checklist.items?.length"
                v-model="checklist.items" 
                item-key="id"
                handle=".drag-handle"
                class="space-y-4"
              >
                <template #item="{element: item}">
                  <div class="flex items-center">
                    <button class="drag-handle p-2 text-gray-400 hover:text-gray-600 cursor-move">
                      <span class="material-icons">drag_indicator</span>
                    </button>
                    <div class="flex-1">
                      <ChecklistItem 
                        :item="item" 
                        :parentChecklist="checklist"
                        @update="updateDirectItem($event)" 
                        @delete="deleteDirectItem($event)" 
                      />
                    </div>
                  </div>
                </template>
              </draggable>
            </div>

            <!-- Sections -->
            <div class="space-y-8">
              <draggable 
                v-if="checklist.sections?.length" 
                v-model="checklist.sections"
                item-key="id"
                handle=".drag-handle"
                class="divide-y divide-gray-200"
              >
                <template #item="{element: section}">
                  <div class="pt-8 first:pt-0">
                    <!-- Section Container with distinct styling -->
                    <div class="border-2 border-gray-300 rounded-xl p-6 bg-white shadow-sm">
                      <!-- Section Header -->
                      <div class="flex flex-col md:flex-row items-center gap-2 mb-4 bg-gray-50 p-3 rounded-lg">
                        <div class="flex w-full md:w-auto flex-grow">
                        <div>
                        <button 
                          @click="toggleSection(section.id)"
                          class="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                          <span class="material-icons text-2xl">
                            {{ collapsedSections.has(section.id) ? 'expand_more' : 'expand_less' }}
                          </span>
                        </button>
                        <button class="drag-handle p-2 text-gray-400 hover:text-gray-600 cursor-move">
                          <span class="material-icons">drag_indicator</span>
                        </button>
                      </div>
                        <input 
                          v-model="section.title" 
                          class="flex-1 p-3 border rounded-lg bg-white text-lg font-medium"
                          placeholder="Section Title"
                        />
                      </div>
                        <!-- Action Buttons -->
                        <div class="flex flex-wrap items-center gap-2 self-end">
                          <button 
                            @click="addDirectItemToSection(section)"
                            class="btn-action bg-blue-50 text-blue-600"
                          >
                            <span class="material-icons">add</span>
                            <span class="hidden lg:inline">Add Item</span>
                          </button>
                          <button 
                            @click="addSubsection(section)"
                            class="btn-action bg-blue-100 text-blue-700"
                          >
                            <span class="material-icons">create_new_folder</span>
                            <span class="hidden lg:inline">Add Subsection</span>
                          </button>
                          <button 
                            @click="deleteSection(section.id)"
                            class="btn-action bg-red-50 text-red-600"
                          >
                            <span class="material-icons">delete</span>
                          </button>
                        </div>
                      </div>

                      <!-- Section Content -->
                      <div v-show="!collapsedSections.has(section.id)"
                          class="space-y-6 transition-all duration-200">
                        <!-- Direct Items in Section -->
                        <div v-if="section.items?.length" class="ml-4 space-y-4 border-l-4 border-blue-100 pl-4">
                          <draggable 
                            v-model="section.items" 
                            item-key="id"
                            handle=".drag-handle"
                            class="space-y-4"
                          >
                            <template #item="{element: item}">
                              <div class="flex items-center">
                                <button class="drag-handle p-2 text-gray-400 hover:text-gray-600 cursor-move">
                                  <span class="material-icons">drag_indicator</span>
                                </button>
                                <div class="flex-1">
                                  <ChecklistItem 
                                    :item="item" 
                                    :parentChecklist="checklist"
                                    @update="updateSectionItem(section, $event)" 
                                    @delete="deleteSectionItem(section.id, $event)" 
                                  />
                                </div>
                              </div>
                            </template>
                          </draggable>
                        </div>

                        <!-- Subsections with improved visual hierarchy -->
                        <div v-if="section.subsections?.length" class="space-y-4">
                          <draggable 
                            v-model="section.subsections" 
                            item-key="id"
                            handle=".drag-handle"
                            class="space-y-6"
                          >
                            <template #item="{element: subsection}">
                              <div class="border-l-4 border-blue-300 bg-blue-50 rounded-lg p-4 ml-6">
                                <!-- Subsection Header -->
                                <div class="flex items-center gap-2 mb-4 bg-white p-3 rounded-lg shadow-sm">
                                  <button 
                                    @click="toggleSubsection(subsection.id)"
                                    class="p-2 text-gray-600 hover:text-gray-800"
                                  >
                                    <span class="material-icons">
                                      {{ collapsedSubsections.has(subsection.id) ? 'expand_more' : 'expand_less' }}
                                    </span>
                                  </button>
                                  <button class="drag-handle p-2 text-gray-400 hover:text-gray-600 cursor-move">
                                    <span class="material-icons">drag_indicator</span>
                                  </button>
                                  <input 
                                    v-model="subsection.title" 
                                    class="flex-1 p-2 border rounded-lg text-md font-medium"
                                    placeholder="Subsection Title"
                                  />
                                  <div class="flex gap-2">
                                    <button 
                                      @click="addItem(subsection)"
                                      class="btn-action bg-blue-50 text-blue-600"
                                    >
                                      <span class="material-icons">add_circle_outline</span>
                                      <span class="hidden lg:inline">Add Item</span>
                                    </button>
                                    <button 
                                      @click="deleteSubsection(section.id, subsection.id)"
                                      class="btn-action bg-red-50 text-red-600"
                                    >
                                      <span class="material-icons">delete</span>
                                    </button>
                                  </div>
                                </div>

                                <!-- Subsection Items -->
                                <div v-show="!collapsedSubsections.has(subsection.id)"
                                    class="ml-4 space-y-4 transition-all duration-200">
                                  <draggable 
                                    v-model="subsection.items" 
                                    item-key="id"
                                    handle=".drag-handle"
                                    class="pl-4 space-y-4"
                                  >
                                    <template #item="{element: item}">
                                      <div class="flex items-center">
                                        <button class="drag-handle p-2 text-gray-400 hover:text-gray-600 cursor-move">
                                          <span class="material-icons">drag_indicator</span>
                                        </button>
                                        <div class="flex-1">
                                          <ChecklistItem 
                                            :item="item" 
                                            :parentChecklist="checklist"
                                            @update="updateItem(subsection, $event)" 
                                            @delete="deleteItem(subsection.id, $event)" 
                                          />
                                        </div>
                                      </div>
                                    </template>
                                  </draggable>
                                </div>
                              </div>
                            </template>
                          </draggable>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>

              <!-- Combined Add Section and Add Item Buttons -->
              <div class="mt-4 flex justify-end gap-4">
                <button 
                  @click="addDirectItem"
                  class="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition duration-200 flex items-center gap-2"
                >
                  <span class="material-icons">add</span>
                  Agregar Elemento
                </button>
                <button 
                  @click="addSection"
                  class="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition duration-200 flex items-center gap-2"
                >
                  <span class="material-icons">folder</span>
                  Agregar Sección
                </button>
              </div>
            </div>
          </div>

          <!-- Save Button -->
          <div class="mt-8 flex justify-end border-t pt-6">
            <button 
              @click="saveChecklist()"
              class="bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition duration-200 shadow-sm flex items-center gap-2"
            >
              <span class="material-icons">save</span>
              Guardar Cambios
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Toast -->
    <div v-if="showSuccess" 
         class="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 transition-all duration-300"
         :class="{ 'opacity-0 translate-y-full': !showSuccess }"
    >
      <span class="material-icons">check_circle</span>
      ¡Cambios guardados exitosamente!
    </div>

    <!-- Add Error Toast below Success Toast -->
    <div v-if="showErrorToast" 
         class="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 transition-all duration-300"
         :class="{ 'opacity-0 translate-y-full': !showErrorToast }"
    >
      <span class="material-icons">error</span>
      {{ errorMessage }}
    </div>
  </div>
  <div v-else class="flex justify-center items-center h-screen">
    <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
  </div>

  <DefaultOptionsSelector
    v-if="showDefaultOptions"
    @select="setDefaultOptions"
    @cancel="showDefaultOptions = false"
  />
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import ChecklistTreeView from './ChecklistTreeView.vue';
import { useStore } from '../store/store';
import { useRoute, useRouter } from 'vue-router';
import { v4 as uuidv4 } from 'uuid';
import ChecklistItem from '../components/ChecklistItem.vue';
import DefaultOptionsSelector from './DefaultOptionsSelector.vue';
import draggable from 'vuedraggable';

export default {
  components: { 
    ChecklistItem,
    DefaultOptionsSelector,
    draggable,
    ChecklistTreeView
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const checklist = ref(null);
    const showDefaultOptions = ref(false);
    const defaultOptions = ref([]);
    const showSuccess = ref(false);
    const showErrors = ref(false);
    const collapsedSections = ref(new Set());
    const collapsedSubsections = ref(new Set());

    // Add refs for form elements
    const titleInput = ref(null);
    const numberInput = ref(null);

    const showErrorToast = ref(false);
    const errorMessage = ref('');

    onMounted(async () => {
      const id = route.params.id;
      
      // Check localStorage for new checklist data
      const savedData = localStorage.getItem('newChecklistData');
      if (savedData) {
        const { initialData } = JSON.parse(savedData);
        checklist.value = initialData;
        // Clear the data from localStorage
        localStorage.removeItem('newChecklistData');
      } else {
        // Load existing checklist
        await store.loadChecklists();
        const loadedChecklist = store.getChecklistById(id);
        if (loadedChecklist) {
          checklist.value = {
            ...loadedChecklist,
            items: Array.isArray(loadedChecklist.items) ? loadedChecklist.items : [],
            sections: Array.isArray(loadedChecklist.sections) ? loadedChecklist.sections : []
          };
        }
      }

      // Initialize rev if needed
      if (checklist.value && !checklist.value.rev) {
        checklist.value.rev = 1;
      }
    });

    onUnmounted(() => {
    });

    const setDefaultOptions = (options) => {
      defaultOptions.value = options;
      showDefaultOptions.value = false;
    };

    const addItem = (subsection) => {
      if (!subsection.items) {
        subsection.items = [];
      }
      const newItem = {
        id: uuidv4(),
        description: 'New Item',
        options: checklist.value.defaultOptions ? 
          checklist.value.defaultOptions.map(opt => ({
            ...opt,
            id: uuidv4()
          })) : []
      };
      subsection.items.push(newItem);
      
      // Scroll to bottom after a short delay
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    };

    const updateItem = (subsection, updatedItem) => {
      const index = subsection.items.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        subsection.items[index] = updatedItem;
      }
    };

    const deleteItem = (subsectionId, itemId) => {
      for (const section of checklist.value.sections) {
        const subsection = section.subsections.find(sub => sub.id === subsectionId);
        if (subsection) {
          subsection.items = subsection.items.filter(item => item.id !== itemId);
          break;
        }
      }
    };

    const scrollToError = () => {
      nextTick(() => {
        if (!checklist.value.title) {
          const titleEl = document.querySelector('[data-field="title"]');
          if (titleEl) {
            titleEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            titleEl.focus();
          }
          return;
        }
        if (!checklist.value.number) {
          const numberEl = document.querySelector('[data-field="number"]');
          if (numberEl) {
            numberEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            numberEl.focus();
          }
        }
      });
    };

    const validateChecklist = () => {
      let errorMessages = [];
      
      if (!checklist.value.number) {
        errorMessages.push('El número es requerido');
      }
      if (!checklist.value.title) {
        errorMessages.push('El título es requerido');
      }

      if (errorMessages.length > 0) {
        showErrors.value = true;
        scrollToError();
        showError(errorMessages.join(', '));
        return false;
      }
      
      showErrors.value = false;
      return true;
    };

    const saveChecklist = async () => {
      try {
        if (!validateChecklist()) {
          return;
        }
        
        await store.updateChecklist(checklist.value);
        showSuccess.value = true;
        setTimeout(() => {
          showSuccess.value = false;
        }, 3000);
      } catch (error) {
        console.error('Error saving checklist:', error);
      }
    };

    const handleDragEnd = () => {
      // Don't save automatically anymore
      console.log('Items reordered');
    };

    const goBack = () => {
      // If it's a new unsaved checklist, just navigate away
      if (route.state?.isNew) {
        router.push({ name: 'Home' });
        return;
      }
      
      // For existing checklists, normal navigation
      router.push({ name: 'Home' });
    };

    const incrementRev = () => {
      checklist.value.rev = (checklist.value.rev || 1) + 1;
    };

    const decrementRev = () => {
      if ((checklist.value.rev || 1) > 1) {
        checklist.value.rev = checklist.value.rev - 1;
      }
    };

    const addSection = () => {
      if (!checklist.value.sections) {
        checklist.value.sections = [];
      }
      checklist.value.sections.push({
        id: uuidv4(),
        title: '',
        subsections: []
      });
    };

    const addSubsection = (section) => {
      if (!section.subsections) {
        section.subsections = [];
      }
      section.subsections.push({
        id: uuidv4(),
        title: '',
        items: []
      });
    };

    const deleteSection = (sectionId) => {
      checklist.value.sections = checklist.value.sections.filter(
        section => section.id !== sectionId
      );
    };

    const deleteSubsection = (sectionId, subsectionId) => {
      const section = checklist.value.sections.find(s => s.id === sectionId);
      if (section) {
        section.subsections = section.subsections.filter(
          sub => sub.id !== subsectionId
        );
      }
    };

    const addDirectItem = () => {
      if (!checklist.value.items) {
        checklist.value.items = [];
      }
      
      const newItem = {
        id: uuidv4(),
        description: 'New Item',
        options: checklist.value.defaultOptions ? 
          checklist.value.defaultOptions.map(opt => ({
            ...opt,
            id: uuidv4()
          })) : []
      };
      
      checklist.value.items.push(newItem);
    };

    const updateDirectItem = (updatedItem) => {
      const index = checklist.value.items.findIndex(item => item.id === updatedItem.id);
      if (index !== -1) {
        checklist.value.items[index] = updatedItem;
      }
    };

    const deleteDirectItem = (itemId) => {
      checklist.value.items = checklist.value.items.filter(item => item.id !== itemId);
    };

    const addDirectItemToSection = (section) => {
      if (!section.items) {
        section.items = [];
      }
      
      const newItem = {
        id: uuidv4(),
        description: 'New Item',
        options: checklist.value.defaultOptions ? 
          checklist.value.defaultOptions.map(opt => ({
            ...opt,
            id: uuidv4()
          })) : []
      };
      
      section.items.push(newItem);
    };

    const updateSectionItem = (section, updatedItem) => {
      const index = section.items.findIndex(item => item.id === updatedItem.id);
      if (index !== -1) {
        section.items[index] = updatedItem;
      }
    };

    const deleteSectionItem = (sectionId, itemId) => {
      const section = checklist.value.sections.find(s => s.id === sectionId);
      if (section) {
        section.items = section.items.filter(item => item.id !== itemId);
      }
    };

    const toggleSection = (sectionId) => {
      if (collapsedSections.value.has(sectionId)) {
        collapsedSections.value.delete(sectionId);
      } else {
        collapsedSections.value.add(sectionId);
      }
    };

    const toggleSubsection = (subsectionId) => {
      if (collapsedSubsections.value.has(subsectionId)) {
        collapsedSubsections.value.delete(subsectionId);
      } else {
        collapsedSubsections.value.add(subsectionId);
      }
    };

    const showError = (message) => {
      errorMessage.value = message;
      showErrorToast.value = true;
      setTimeout(() => {
        showErrorToast.value = false;
        errorMessage.value = '';
      }, 3000);
    };

    return {
      checklist,
      addItem,
      updateItem,
      deleteItem,
      saveChecklist,
      goBack,
      showDefaultOptions,
      setDefaultOptions,
      handleDragEnd,
      showSuccess,
      showErrors,
      incrementRev,
      decrementRev,
      addSection,
      addSubsection,
      deleteSection,
      deleteSubsection,
      addDirectItem,
      updateDirectItem,
      deleteDirectItem,
      addDirectItemToSection,
      updateSectionItem,
      deleteSectionItem,
      collapsedSections,
      collapsedSubsections,
      toggleSection,
      toggleSubsection,
      titleInput,
      numberInput,
      showErrorToast,
      errorMessage
    };
  }
};
</script>

<style scoped>
.checklist-item {
  scroll-margin-top: 2rem;
}

.success-toast-enter-active,
.success-toast-leave-active {
  transition: all 0.3s ease;
}

.success-toast-enter-from,
.success-toast-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.fixed {
  z-index: 50;
}

@media (max-width: 640px) {
  .container {
    padding-bottom: 4rem;
  }
}

.divide-y > * + * {
  border-top: 1px solid theme('colors.gray.200');
}

/* Add error toast animations */
.error-toast-enter-active,
.error-toast-leave-active {
  transition: all 0.3s ease;
}

.error-toast-enter-from,
.error-toast-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.btn-action {
  @apply flex items-center gap-1 px-3 py-2 rounded-lg transition-colors duration-200;
}

@media (max-width: 640px) {
  .btn-action {
    @apply p-2;
  }
  
  .fixed {
    @apply w-11/12 mx-auto;
  }
  
  .grid-cols-1 {
    @apply gap-2;
  }
}

/* Improve drag and drop visual feedback */
.sortable-ghost {
  @apply opacity-50 bg-gray-100;
}

.sortable-drag {
  @apply shadow-lg;
}

/* Add smooth transitions */
.draggable-transition {
  @apply transition-all duration-200;
}

/* Better mobile spacing */
@media (max-width: 640px) {
  .p-6 {
    @apply p-3;
  }
  
  .mb-6 {
    @apply mb-4;
  }
  
  .space-y-8 > * + * {
    @apply mt-4;
  }
  
  .rounded-xl {
    @apply rounded-lg;
  }
}

/* Add these new mobile-specific styles */
@media (max-width: 640px) {
  .draggable-transition {
    width: 100%;
  }

  /* Adjust padding and margins for mobile */
  .p-6 {
    @apply p-2;
  }

  /* Make sure items don't overflow on mobile */
  .flex-1 {
    min-width: 0; /* Prevents flex items from overflowing */
  }

  /* Adjust the item container spacing */
  .space-y-4 {
    @apply space-y-2;
  }

  /* Make the grid more compact on mobile */
  .grid.gap-6 {
    @apply gap-2;
  }

  /* Ensure proper padding in sections */
  .border-2.border-gray-300.rounded-xl.p-6 {
    @apply p-3;
  }

  /* Adjust the nested structure padding */
  .ml-4.space-y-4.border-l-4 {
    @apply ml-2 pl-2;
  }

  /* Make drag handle more compact */
  .drag-handle {
    @apply p-1;
  }

  /* Adjust spacing for subsections */
  .border-l-4.border-blue-300.bg-blue-50.rounded-lg.p-4.ml-6 {
    @apply p-2 ml-2;
  }

  /* Make buttons more compact on mobile */
  .btn-action {
    @apply px-2 py-1 text-sm;
  }

  /* Ensure inputs don't overflow */
  input, 
  textarea {
    @apply max-w-full box-border;
    width: 100% !important;
  }
}
</style>